import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/utils/utils";
import { TEXT_EDITOR_STYLING_CLASSES } from "../utils/textEditorStyles";
import { omitProps } from "@/components/ui/omit-props";

interface TextEditorMarkdownDisplayProps {
  content: string;
  className?: string;
}

const TextEditorMarkdownDisplay: React.FC<TextEditorMarkdownDisplayProps> = ({
  content,
  className = ""
}) => {
  if (!content || content.trim() === "") {
    return null;
  }

  return (
    <div
      className={cn(
        "text-primary text-base pl-2 pr-3 pb-1",
        ...TEXT_EDITOR_STYLING_CLASSES,
        className
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Paragraphs - match TextEditor styling exactly
          p(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <p
                className="text-base font-normal leading-5 text-primary my-0"
                {...props}
              >
                {children}
              </p>
            );
          },
          // Headings - match TextEditor styling exactly
          h1(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <h1
                className="text-xl font-semibold capitalize text-primary my-4"
                {...props}
              >
                {children}
              </h1>
            );
          },
          h2(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <h2
                className="text-[18px] font-semibold text-primary my-3"
                {...props}
              >
                {children}
              </h2>
            );
          },
          h3(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <h3
                className="text-lg font-semibold leading-[22px] text-primary my-2"
                {...props}
              >
                {children}
              </h3>
            );
          },
          // Lists - match TextEditor styling exactly
          ul(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <ul className="text-primary ml-6" {...props}>
                {children}
              </ul>
            );
          },
          ol(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <ol className="text-primary ml-6" {...props}>
                {children}
              </ol>
            );
          },
          li(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <li className="text-primary ml-0" {...props}>
                {children}
              </li>
            );
          },
          // Links - match TextEditor styling exactly
          a(allProps) {
            const { href, children, ...props } = omitProps(allProps, "node");
            if (!href) return <span>{children}</span>;
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-link underline cursor-pointer hover:text-link-hover"
                {...props}
              >
                {children}
              </a>
            );
          },
          // Strong and emphasis - match TextEditor styling
          strong(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <strong className="font-semibold" {...props}>
                {children}
              </strong>
            );
          },
          em(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <em className="italic" {...props}>
                {children}
              </em>
            );
          },
          // Code - basic styling to match editor
          code(allProps) {
            const { className, children, ...props } = omitProps(
              allProps,
              "node"
            );
            const match = /language-(\w+)/.exec(className || "");
            const isCodeBlock =
              match && typeof children === "string" && children.includes("\n");

            if (!isCodeBlock) {
              return (
                <code
                  className="bg-surface-elevation1 px-1 py-0.5 rounded text-base font-mono"
                  {...props}
                >
                  {children}
                </code>
              );
            }
            return (
              <pre className="p-4 overflow-x-auto bg-surface-elevation1 text-base font-mono rounded">
                <code {...props}>
                  {typeof children === "string"
                    ? children.replace(/\n$/, "")
                    : children}
                </code>
              </pre>
            );
          },
          // Blockquotes
          blockquote(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <blockquote
                className="border-l-4 border-border-border1 pl-4 py-1 my-2 italic text-primary"
                {...props}
              >
                {children}
              </blockquote>
            );
          },
          // Horizontal rule
          hr(allProps) {
            const props = omitProps(allProps, "node");
            return (
              <hr className="my-2 border-t border-border-border1" {...props} />
            );
          }
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default TextEditorMarkdownDisplay;
